import React from 'react';
import _ from 'underscore';

import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';

import { SessionManagementService } from '../../../sessionManagement/SessionManagementService';
import { GeneralSiteSettingsCoordinator } from '../../../settings/general-settings/components/site-settings/GeneralSiteSettingsCoordinator';
import { ServerSettings } from '../../../settings/services/ServerSettings';
import {
  ISiteAuthenticationSettings,
  SiteAuthenticationSettingsService,
} from '../../../settings/siteAuthSettings/services/SiteAuthenticationSettingsService';
import { ISiteSettingsForServerAdmin } from '../../../settings/types/ISite';
import { ISiteSettingsForSiteAdmin } from '../../../settings/types/ISiteSettingsForSiteAdmin';
import { IEditableServerSettings } from '../../../ts/ServerApi/types/IEditableServerSettings';
import { Sites } from '../../../ts/siteManagement/services/resources/Sites';
import { RouterError } from '../RouterHelpers/RouterError';

export interface GeneralSiteSettingsSubspaceOwnProps {
  readonly siteName: string;
  readonly urls: SpacesSiteUrls;
  readonly server: SessionManagementService;
  readonly isSessionMultiSite: boolean;
  readonly isSessionServerAdmin: boolean;
}

export interface GeneralSiteSettingsSubspaceServicesProps {
  readonly serverSettingsService: ServerSettings;
}

export type GeneralSiteSettingsSubspaceProps = GeneralSiteSettingsSubspaceOwnProps & GeneralSiteSettingsSubspaceServicesProps;

export interface GeneralSiteSettingsSubspaceState {
  readonly hasDataToRender: boolean;
  readonly hasError: boolean;
  readonly serverSettings: IEditableServerSettings;
  readonly siteSettings: ISiteSettingsForSiteAdmin;
  readonly siteSettingsForServerAdmin: ISiteSettingsForServerAdmin;
  readonly siteAuthenticationSettings: ISiteAuthenticationSettings;
}

export class GeneralSiteSettingsSubspace extends React.PureComponent<GeneralSiteSettingsSubspaceProps, GeneralSiteSettingsSubspaceState> {

  private unmounted: boolean = false;

  private initialState: GeneralSiteSettingsSubspaceState = {
    hasDataToRender: false,
    hasError: false,
    serverSettings: undefined,
    siteSettings: undefined,
    siteAuthenticationSettings: undefined,
    siteSettingsForServerAdmin: undefined
  };

  public constructor(props: GeneralSiteSettingsSubspaceProps) {
    super(props);
    this.state = this.initialState;
  }

  public componentDidMount() {
    this.getData();
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidUpdate(prevProps: GeneralSiteSettingsSubspaceProps) {
    if (!_.isUndefined(this.props.siteName) && prevProps.siteName !== this.props.siteName) {
      this.updateState(this.initialState);
      this.getData();
      return;
    }

    if (!_.isUndefined(this.state.serverSettings)
      && !_.isUndefined(this.state.siteSettings)
      && !_.isUndefined(this.state.siteAuthenticationSettings)
      && !_.isUndefined(this.state.siteSettingsForServerAdmin)) {
        this.updateState({hasDataToRender: true});
    }
  }

  public render() {
    if (!this.state.hasDataToRender) {
      return null;
    } else if (this.state.hasError) {
      return <RouterError errorType={'default'} urls={this.props.urls} />;
    }

    return (
      <div className={'tb-stylish-settings'}>
        <GeneralSiteSettingsCoordinator {...this.state}/>
      </div>
    );
  }

  private updateState<K extends keyof GeneralSiteSettingsSubspaceState>(newState: Pick<GeneralSiteSettingsSubspaceState, K>, callback?: () => void): void {
    if (!this.unmounted) {
      this.setState(newState, callback);
    }
  }

  private getData() {
    this.getServerSettings();
    this.getSiteSettings();
    this.getSiteSettingsForServerAdmin();
    this.getSiteAuthenticationSettings();
  }

  private getServerSettings = async () => {
    if (this.props.isSessionServerAdmin) {
      try {
        const serverSettings = await this.props.serverSettingsService.fetch();
        this.updateState({serverSettings: serverSettings});
      } catch (e) {
        this.updateState({hasError: true});
      }
    } else {
      this.updateState({serverSettings: null});
    }
  }

  private getSiteSettings = async () => {
    try {
      await this.props.server.getSessionInfoPromise();
      const siteSettings = await Sites.instance.getCurrentSiteSettingsForSiteAdmin();
      this.updateState({siteSettings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private getSiteSettingsForServerAdmin = async () => {
    if (this.props.isSessionServerAdmin) {
      try {
        await this.props.server.getSessionInfoPromise();
        const serverSettings = await Sites.instance.getCurrentSiteSettingsForServerAdmin();
        this.updateState({siteSettingsForServerAdmin: serverSettings});
      } catch (e) {
        this.updateState({hasError: true});
      }
    } else {
      this.updateState({siteSettingsForServerAdmin: null});
    }
  }

  private getSiteAuthenticationSettings = async () => {
    try {
      const siteAuthSettings = await SiteAuthenticationSettingsService.getSiteAuthenticationSettings(/* refresh */ true);
      this.updateState({siteAuthenticationSettings: siteAuthSettings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }
}
